import moment from 'moment';
import {
	getPostponePreference,
	isRememberPostponeEnabled,
	setPostponePreference
} from 'App/babel/enum/activityOutcome';

/**
 * Save the rep's postpone preset. A no-op unless they ticked "remember" — otherwise a single
 * unusual choice would become the default for every following activity.
 *
 * When "later today" is picked with a specific time, the offset from now is stored too, so the
 * next suggestion is that far ahead again rather than a time that has since passed.
 */
export const savePostponePreference = (preset: string, selectedTime?: Date | null): void => {
	if (!isRememberPostponeEnabled()) {
		return;
	}

	const preference = { preset, remember: true } as { preset: string; remember: true; timeOffsetMinutes?: number };

	if (preset === 'currentDay' && selectedTime) {
		const now = moment();
		const selected = moment(selectedTime);
		// Only store an offset for a time later today; anything else is not a "later today" pick.
		if (selected.isSame(now, 'day')) {
			const offsetMinutes = selected.diff(now, 'minutes');
			if (offsetMinutes > 0) {
				preference.timeOffsetMinutes = offsetMinutes;
			}
		}
	}

	setPostponePreference(preference);
};

/**
 * The time to suggest for "later today", snapped to a whole hour so it lines up with the slots
 * the dropdown offers. Null when the rep did not opt in, nothing is stored, or the offset would
 * land past the end of the day.
 */
export const getSuggestedTimeFromPreference = (): Date | null => {
	const preference = getPostponePreference();

	if (preference?.remember !== true || preference.preset !== 'currentDay' || !preference.timeOffsetMinutes) {
		return null;
	}

	const now = moment();
	const target = now.clone().add(preference.timeOffsetMinutes, 'minutes');

	let snapped = target.clone().startOf('hour');
	if (target.minute() >= 30) {
		snapped.add(1, 'hour');
	}

	// The first slot the dropdown offers is the next full hour after now.
	const firstSlot = now.clone().startOf('hour').add(1, 'hour');
	if (snapped.isBefore(firstSlot)) {
		snapped = firstSlot;
	}

	return snapped.isAfter(now.clone().endOf('day')) ? null : snapped.toDate();
};
